/**
 * Task notices: subscribers subscribe to notices about task status.
 *
 * A notice is raised when a task reaches a state worth telling the user about. Every consumer —
 * inbox list, menu badge, notifications — derives from this selector, so they cannot disagree.
 * Notices are derived state, not delivered events: they appear, disappear when the task moves on,
 * and can be raised again later.
 */

import type { TaskProgressState } from '../chat/types';

import type { UnifiedTask } from './store';

/** Why a task is worth telling the user about. Extend this union to add a reason. */
export type TaskNoticeReason = 'needs-input';

/**
 * A task in a state worth telling the user about. Consumers decide how to present a notice — an
 * inbox row, a menu badge count, a notification — and may filter on `reason` without re-deriving
 * notice policy.
 */
export type TaskNotice = {
	taskId: string;
	conversationId: string;
	/** The message holding the required action. Never the originating or completion message. */
	currentMessageId: string;
	agentId: string | null;
	title: string;
	reason: TaskNoticeReason;
	/** Retained for analytics and future per-status differentiation. */
	progressState: TaskProgressState;
	/** When the notice was raised, if a verified timestamp exists. */
	raisedAt: string | undefined;
	/** Ordering fallback only — changes on unrelated updates, so never show it as notice age. */
	updatedAt: string;
};

/** The single place that decides whether a task status warrants a notice. */
const NOTICE_REASON_BY_PROGRESS_STATE: Partial<Record<TaskProgressState, TaskNoticeReason>> = {
	AwaitingDisambiguation: 'needs-input',
	AwaitingPlanConfirmation: 'needs-input',
	AwaitingActionConfirmation: 'needs-input',
};

/**
 * Contract gap: no task field means "entered the current state" — only `created_at`,
 * `started_at`, `updated_at` and `completed_at` exist. Substituting `updatedAt` would claim
 * precision the contract lacks, so age stays unknown until a dedicated field lands. This is the
 * single place to populate it; ordering and row age then pick it up automatically.
 */
const resolveVerifiedRaisedAt = (task: UnifiedTask): string | undefined => {
	void task;
	return undefined;
};

/**
 * Maps a task to its notice, or `undefined` when none applies.
 *
 * Tasks missing the identifiers needed to open the blocking message raise no notice rather than
 * appearing as dead ends. Every reason needs a target today; revisit if a purely informational
 * reason is added.
 */
const toTaskNotice = (task: UnifiedTask): TaskNotice | undefined => {
	const { progressState, conversationId, currentMessageId } = task;
	const reason = NOTICE_REASON_BY_PROGRESS_STATE[progressState];

	if (!reason || !conversationId || !currentMessageId) {
		return undefined;
	}

	return {
		taskId: task.taskId,
		conversationId,
		currentMessageId,
		agentId: task.agentId,
		title: task.title,
		reason,
		progressState,
		raisedAt: resolveVerifiedRaisedAt(task),
		updatedAt: task.updatedAt,
	};
};

/** Unparseable timestamps compare equal so they fall through to the next tie-break. */
const toComparableTime = (iso: string): number => {
	const ms = Date.parse(iso);
	return Number.isNaN(ms) ? 0 : ms;
};

/**
 * Newest raised notice first, then newest store update, then `taskId`. Notices with a known
 * `raisedAt` always rank above those without.
 */
const compareTaskNotices = (a: TaskNotice, b: TaskNotice): number => {
	const aRaisedAt = a.raisedAt;
	const bRaisedAt = b.raisedAt;

	if (aRaisedAt !== undefined || bRaisedAt !== undefined) {
		if (aRaisedAt === undefined) {
			return 1;
		}
		if (bRaisedAt === undefined) {
			return -1;
		}
		const raisedDelta = toComparableTime(bRaisedAt) - toComparableTime(aRaisedAt);
		if (raisedDelta !== 0) {
			return raisedDelta;
		}
	}

	const updatedDelta = toComparableTime(b.updatedAt) - toComparableTime(a.updatedAt);
	if (updatedDelta !== 0) {
		return updatedDelta;
	}

	if (a.taskId === b.taskId) {
		return 0;
	}
	return a.taskId < b.taskId ? -1 : 1;
};

/**
 * Derives the current set of task notices.
 *
 * One notice per `taskId` (last record wins), so replayed records cannot produce two. Tasks that
 * move to a state warranting no notice drop out; a later qualifying transition raises a new
 * notice. Ordering is total, so the result is stable across recomputation.
 */
export const selectTaskNotices = (tasks: Iterable<UnifiedTask>): TaskNotice[] => {
	const noticesByTaskId = new Map<string, TaskNotice>();

	for (const task of tasks) {
		const notice = toTaskNotice(task);
		if (notice) {
			noticesByTaskId.set(notice.taskId, notice);
		} else {
			noticesByTaskId.delete(task.taskId);
		}
	}

	return Array.from(noticesByTaskId.values()).sort(compareTaskNotices);
};
